/** @jsxImportSource @emotion/react */
import styled from '@emotion/styled'
import DataTable from 'components/atoms/DataTable'
import Toast from 'components/atoms/Toast'
import { TableWrapper } from 'components/atoms/TableWrapper'
import { confirm } from 'components/molecules/Confirmation'
import React from 'react'
import {
  useCreatePrepaymentStripeSessionMutation,
  useDeleteBillingPrepaymentMutation,
  useGetPatientBillingPrepaymentsQuery,
  useRefundBillingPrepaymentMutation,
} from 'store/services/billingPrepayments'
import { downloadEobPdf } from 'helpers/eob'
import {
  useGeneratePaymentReceiptMutation,
  useLazyGetPaymentReceiptDownloadQuery,
} from 'store/services/paymentReceipts'
import { useGetPatientBillingPaymentsQuery } from 'store/services/billingPayments'
import { useGetPatientBillingChargesQuery, useSubmitBillingChargesMutation, } from 'store/services/billingCharges'
import { BillingChargeDto, ChargeAccountRow, ChargeAccountServiceLine, ChargeAccountTransaction, } from 'store/types'
import { EditablePrepayment, ReceivePrepaymentModal } from './ReceivePrepaymentModal'
import { ApplyPrepaymentModal } from './ApplyPrepaymentModal'
import { RefundConfirmModal } from './RefundConfirmModal'
import { ChargeAccountTable } from './ChargeAccountTable'
import { CreatePaymentModal } from './CreatePaymentModal'
import {
  describeCobSubmissionError,
  getCobConfirmOptions,
  getCobResubmitConfirmOptions,
  isPaperClaim,
} from '../billingCharge/utils'
import { useLabPermission } from 'hooks/useLabPermission'
import { UserPermission } from '../user/permissions'
import { formatEasternDate } from 'helpers/dates'
import { formatPrepaymentDate } from './prepaymentDate'

const StyledTableWrapper = styled(TableWrapper)`
  th {
    padding: 0.75rem 0.5rem;
    font-weight: 600;
    font-size: 0.875rem;
    background-color: #f8f9fa;
  }
  th:last-of-type {
    text-align: right;
  }
  td {
    vertical-align: middle;
    padding: 0.75rem 0.5rem;
    font-size: 0.9rem;
  }
`

const ScrollContainer = styled.div<{ height?: number }>`
  height: ${({ height = 360 }) => `${height}px`};
  overflow: auto;
`

type Props = {
  patientId?: string
}

const formatDate = (date?: string | Date | null) => formatEasternDate(date, '--')

const money = (value?: number | null) => `$${Number(value || 0).toFixed(2)}`

const fullName = (firstName?: string | null, lastName?: string | null) => [firstName, lastName]
  .filter(Boolean)
  .join(' ')
  .trim()

const formatPrepaymentMethod = (method?: string | null) => {
  if (!method) return '--'

  const labels: Record<string, string> = {
    STRIPE: 'Stripe',
    CREDIT_CARD: 'Credit Card',
    CASH: 'Cash',
    ACH: 'ACH',
    CHECK: 'Check',
    CARE_CREDIT: 'Care Credit',
    MONEY_ORDER: 'Money Order',
    OTHER: 'Other',
  }

  return labels[method] || method
}

export const BillingPaymentsListing = ({ patientId }: Props) => {
  const numericPatientId = Number(patientId)
  const [receiveOpen, setReceiveOpen] = React.useState(false)
  const [editTarget, setEditTarget] = React.useState<EditablePrepayment | null>(null)
  const [applyOpen, setApplyOpen] = React.useState<{ open: boolean; id?: string }>({ open: false })
  const [refundOpen, setRefundOpen] = React.useState<{ id: string; remaining: number } | null>(null)
  // Open with a charge (and the payer whose turn it is) when the inline row
  // hands over an EOB that paid several lines; empty from the button.
  const [createPaymentTarget, setCreatePaymentTarget] = React.useState<
    { chargeId?: string; payerId?: number } | null
  >(null)
  const [eobLoading, setEobLoading] = React.useState(false)
  const canPostPatientPayments = useLabPermission(UserPermission.POST_PATIENT_PAYMENTS)
  const canEditPrepayments = useLabPermission(UserPermission.EDIT_PATIENT_PREPAYMENTS)
  const canPostInsurancePayments = useLabPermission(UserPermission.POST_INSURANCE_PAYMENTS)
  const [createStripeSession] = useCreatePrepaymentStripeSessionMutation()
  const [deletePrepayment] = useDeleteBillingPrepaymentMutation()
  const [refundPrepayment] = useRefundBillingPrepaymentMutation()
  const [submitCharges] = useSubmitBillingChargesMutation()
  const [submittingChargeId, setSubmittingChargeId] = React.useState<string | null>(null)
  const [generateReceipt] = useGeneratePaymentReceiptMutation()
  const [getReceiptDownloadUrl] = useLazyGetPaymentReceiptDownloadQuery()
  const [receiptLoadingId, setReceiptLoadingId] = React.useState<string | null>(null)

  // The simple receipt for one patient payment — proof of payment, nothing
  // clinical — opened in a new tab; it is also listed under Receipts.
  const handlePrepaymentReceipt = async (prepaymentId: string) => {
    setReceiptLoadingId(prepaymentId)

    try {
      const receipt = await generateReceipt({ patientId: numericPatientId, prepaymentId }).unwrap()
      const link = await getReceiptDownloadUrl({ patientId: numericPatientId, receiptId: receipt.id }).unwrap()

      if (link?.url) {
        window.open(link.url, '_blank', 'noopener,noreferrer')?.focus()
      }

      if (!receipt.reused) {
        Toast({ type: 'success', label: `Receipt ${receipt.receiptNumber || ''} generated`.trim() })
      }

      // The row shows the receipt's number and Stripe link once it exists.
      refetchPrePayments()
    } catch (error: any) {
      Toast({ type: 'error', label: error?.data?.message || 'Failed to generate the receipt' })
    } finally {
      setReceiptLoadingId(null)
    }
  }

  const { data: charges = [], refetch: refetchCharges } = useGetPatientBillingChargesQuery(
    { patientId: numericPatientId },
    { skip: !numericPatientId },
  )
  const { data: payments, isLoading: paymentsLoading, refetch: refetchPayments } = useGetPatientBillingPaymentsQuery(
    {
      patientId: numericPatientId,
      page: 0,
      size: 100,
    },
    { skip: !numericPatientId },
  )
  const {
    data: prepayments,
    isLoading: prepaymentsLoading,
    refetch: refetchPrePayments,
  } = useGetPatientBillingPrepaymentsQuery(
    {
      patientId: numericPatientId,
      page: 0,
      size: 100,
      status: ['AVAILABLE', 'REFUNDED'],
      sortField: 'paymentDate',
      sortOrder: 'desc',
    },
    { skip: !numericPatientId },
  )

  const chargesById = React.useMemo(() => {
    const map: Record<string, BillingChargeDto> = {}

    for (const charge of charges || []) {
      map[charge.id] = charge
    }

    return map
  }, [charges])

  const chargeRows: ChargeAccountRow[] = payments?.items || []
  const hasEraPayments = React.useMemo(
    () => chargeRows.some(
      (row: ChargeAccountRow) => row.serviceLines.some(
        (line: ChargeAccountServiceLine) => line.transactions.some(
          (transaction: ChargeAccountTransaction) => transaction.eraClaimId,
        ),
      ),
    ),
    [chargeRows],
  )

  const handlePayWithStripe = async (prepaymentId: string) => {
    try {
      const result = await createStripeSession({ patientId: numericPatientId, prepaymentId }).unwrap()
      if (result.url) {
        window.location.assign(result.url)
      }
    } catch (error) {
      console.error('Failed to create stripe session:', error)
    }
  }

  const handleDeletePrepayment = async (prepaymentId: string) => {
    try {
      await confirm('', {
        title: 'Delete Pre-Payment',
        description: 'Are you sure you want to delete this pre-payment?',
      })
      await deletePrepayment({ patientId: numericPatientId, prepaymentId }).unwrap()
      Toast({ type: 'success', label: 'Pre-payment deleted successfully' })
      refetchPrePayments()
    } catch (error) {
      if (error) {
        console.error('Failed to delete prepayment:', error)
      }
    }
  }

  const handleSubmitCob = async (charge: any, { resubmit = false }: { resubmit?: boolean } = {}) => {
    // The server computes which hop is next (secondary until it has
    // adjudicated, then tertiary) — the row carries it as cobRank.
    const cobRank: 'secondary' | 'tertiary' = charge.cobRank === 'tertiary' ? 'tertiary' : 'secondary'

    try {
      await confirm('', resubmit
        ? getCobResubmitConfirmOptions(charge, cobRank)
        : getCobConfirmOptions(charge, cobRank))
    } catch {
      // User dismissed the confirmation.
      return
    }

    // The COB claim goes to Optum inside this request, so Optum's answer —
    // or its reason for refusing — comes back with it.
    try {
      setSubmittingChargeId(charge.id)
      await submitCharges({ ids: [charge.id], payerRank: cobRank, resubmit }).unwrap()
      Toast({
        type: 'success',
        label: isPaperClaim(charge, cobRank)
          ? `Paper COB claim to the ${cobRank} payer marked as submitted`
          : `COB claim ${resubmit ? 're-submitted' : 'submitted'} to the ${cobRank} payer`,
      })
    } catch (error) {
      Toast({ type: 'error', label: 'COB Claim Submission Failed', extra: describeCobSubmissionError(error) })
    } finally {
      setSubmittingChargeId(null)
      refetchCharges()
    }
  }

  const handleRefundPrepayment = async (note?: string) => {
    if (!refundOpen) {
      return
    }

    try {
      await refundPrepayment({ patientId: numericPatientId, prepaymentId: refundOpen.id, note }).unwrap()
      Toast({ type: 'success', label: 'Pre-payment marked as refunded' })
      setRefundOpen(null)
      refetchPrePayments()
      refetchCharges()
      refetchPayments()
    } catch (error: any) {
      Toast({ type: 'error', label: error?.data?.message || 'Failed to refund pre-payment' })
    }
  }

  const prepaymentsColumns = [
    {
      Header: 'Date',
      accessor: 'paymentDate',
      Cell: ({ row }: any) => <div>{formatPrepaymentDate(row.original)}</div>,
    },
    {
      Header: 'Amount',
      accessor: 'amount',
      Cell: ({ row }: any) => <div>{money(row.original.amount)}</div>,
    },
    {
      Header: 'Method',
      accessor: 'method',
      Cell: ({ row }: any) => (
        <div>
          {formatPrepaymentMethod(row.original.method)}
          {/* Carved out of a statement payment: the patient's own money
              back from a charge, not a new payment received. */}
          {row.original.sourceStatementId && (
            <div css={{ color: '#6c757d', fontSize: 12 }}>statement payment credit</div>
          )}
        </div>
      ),
    },
    {
      Header: 'Status',
      accessor: 'paymentStatus',
      Cell: ({ row }: any) => {
        const refundedBy = fullName(row.original.refundedByFirstName, row.original.refundedByLastName)
        const refundDetails = [
          row.original.refundedAt ? formatDate(row.original.refundedAt) : '',
          refundedBy ? `by ${refundedBy}` : '',
        ].filter(Boolean).join(' ')

        if (row.original.status === 'REFUNDED') {
          return (
            <div>
              <div css={{ color: '#9370FF', fontWeight: 600 }}>REFUNDED</div>
              {refundDetails && <div css={{ color: '#6c757d', fontSize: 12 }}>{refundDetails}</div>}
            </div>
          )
        }

        const isPaid = row.original.paymentStatus === 'PAID'
        // Refunded in part, with credit on it again since (an application returned).
        const partlyRefunded = Number(row.original.refundedAmount || 0) > 0

        return (
          <div>
            <div css={{ color: isPaid ? '#28a745' : '#dc3545', fontWeight: 600 }}>{row.original.paymentStatus}</div>
            {partlyRefunded && (
              <div css={{ color: '#6c757d', fontSize: 12 }}>
                partly refunded{refundDetails ? ` ${refundDetails}` : ''}
              </div>
            )}
          </div>
        )
      },
    },
    {
      Header: 'Remaining',
      accessor: 'remaining',
      Cell: ({ row }: any) => {
        if (Number(row.original.refundedAmount || 0) > 0) {
          // Applied is what is neither refunded nor still unapplied.
          const appliedAmount = Number(
            (
              Number(row.original.amount || 0)
              - Number(row.original.refundedAmount || 0)
              - Number(row.original.remaining || 0)
            ).toFixed(2),
          )

          return (
            <div>
              {money(row.original.remaining)}
              <div css={{ color: '#6c757d', fontSize: 12 }}>
                {appliedAmount > 0 ? `applied ${money(appliedAmount)} · ` : ''}
                refunded {money(row.original.refundedAmount)}
              </div>
            </div>
          )
        }

        return <div>{money(row.original.remaining)}</div>
      },
    },
    {
      Header: 'Actions',
      accessor: 'id',
      disableSortBy: true,
      Cell: ({ row }: any) => {
        const prepayment = row.original
        const isRefunded = prepayment.status === 'REFUNDED'
        return (
          <div css={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            {prepayment.stripeReceiptUrl && (
              <a
                className="btn btn-outline-secondary btn-sm"
                css={{ whiteSpace: 'nowrap' }}
                href={prepayment.stripeReceiptUrl}
                target="_blank"
                rel="noopener noreferrer"
                title="Stripe's own receipt for this card payment"
                onClick={(e) => e.stopPropagation()}>
                View Stripe Receipt
              </a>
            )}
            {/* Statement credit is what the patient overpaid, not something
                typed in: more of it comes back from the charge, or it is
                refunded — never edited. */}
            {canEditPrepayments && !isRefunded && !prepayment.sourceStatementId && (
              <button
                title="Edit prepayment"
                css={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#007bff',
                }}
                onClick={(e) => {
                  e.stopPropagation()
                  setEditTarget({
                    id: prepayment.id,
                    amount: Number(prepayment.amount || 0),
                    method: prepayment.method,
                    paymentStatus: prepayment.paymentStatus,
                    paymentDate: prepayment.paymentDate,
                    checkDate: prepayment.checkDate,
                    checkNumber: prepayment.checkNumber,
                    checkAmount: prepayment.checkAmount,
                    referenceNumber: prepayment.referenceNumber,
                    notes: prepayment.notes,
                  })
                }}>
                <i className="far fa-edit" css={{ fontSize: '1.1rem' }} />
              </button>
            )}
            {canPostPatientPayments && !isRefunded && prepayment.paymentStatus === 'PAID' && (
              <button
                css={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#007bff',
                }}
                onClick={(e) => {
                  e.stopPropagation()
                  setApplyOpen({ open: true, id: prepayment.id })
                }}>
                <i className="far fa-credit-card" css={{ fontSize: '1.1rem' }} />
              </button>
            )}
            {!isRefunded && prepayment.paymentStatus === 'PAID' && (
              <button
                className="btn btn-outline-secondary btn-sm"
                css={{ whiteSpace: 'nowrap' }}
                disabled={receiptLoadingId === prepayment.id}
                title={
                  prepayment.receiptNumber
                    ? `Receipt ${prepayment.receiptNumber}`
                    : 'Print a simple receipt for this payment'
                }
                onClick={(e) => {
                  e.stopPropagation()
                  handlePrepaymentReceipt(prepayment.id)
                }}>
                {receiptLoadingId === prepayment.id ? 'Preparing…' : 'Download Receipt'}
              </button>
            )}
            {canPostPatientPayments &&
              !isRefunded &&
              prepayment.paymentStatus === 'PAID' &&
              Number(prepayment.remaining || 0) > 0 && (
                <button
                  className="btn btn-outline-danger btn-sm"
                  css={{ whiteSpace: 'nowrap' }}
                  onClick={() =>
                    setRefundOpen({
                      id: prepayment.id,
                      remaining: Number(prepayment.remaining || 0),
                    })
                  }>
                  Refund Remaining
                </button>
              )}
            {canPostPatientPayments &&
              prepayment.paymentStatus !== 'PAID' &&
              prepayment.method === 'STRIPE' && (
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => handlePayWithStripe(prepayment.id)}>
                  Pay
                </button>
              )}
            {canPostPatientPayments && prepayment.paymentStatus === 'UNPAID' && (
              <button
                className="btn btn-outline-danger btn-sm"
                onClick={() => handleDeletePrepayment(prepayment.id)}>
                Delete
              </button>
            )}
          </div>
        )
      },
    },
  ]

  return (
    <div css={{ width: '100%', padding: 10 }}>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div css={{ fontWeight: 600, fontSize: 18 }}>Unapplied Payments</div>
        {canPostPatientPayments && (
          <button className="btn btn-primary" onClick={() => setReceiveOpen(true)}>
            Receive Prepayment
          </button>
        )}
      </div>
      <StyledTableWrapper css={{ padding: 0, border: '1px solid #dee2e6', borderRadius: '4px' }}>
        <ScrollContainer height={360}>
          <DataTable
            data={prepayments}
            tableKey="lab_patient_billing_prepayments"
            status={prepaymentsLoading}
            tableColumns={prepaymentsColumns}
            pageSize={100}
            setCurrentPage={() => {}}
            setSortOptions={() => {}}
            entityName="prepayments"
            setPageSize={() => {}}
            searchable={false}
          />
        </ScrollContainer>
      </StyledTableWrapper>

      <div className="d-flex align-items-center justify-content-between mb-3 mt-4">
        <div css={{ fontWeight: 600, fontSize: 18 }}>Active Charges / Payments</div>
        <div>
          <button
            className="btn btn-outline-primary mr-2"
            disabled={eobLoading || !hasEraPayments}
            onClick={async () => {
              setEobLoading(true)
              await downloadEobPdf(
                `/lab/patients/${numericPatientId}/account/payments/eob`,
                `EOB_Patient_${numericPatientId}.pdf`,
              )
              setEobLoading(false)
            }}>
            {eobLoading ? 'Preparing EOB…' : 'Print EOB'}
          </button>
          {canPostInsurancePayments && (
            <button className="btn btn-primary" disabled={!patientId} onClick={() => setCreatePaymentTarget({})}>
              Create Payment
            </button>
          )}
        </div>
      </div>
      <StyledTableWrapper css={{ padding: 0, border: '1px solid #dee2e6', borderRadius: '4px' }}>
        <ScrollContainer height={640}>
          <ChargeAccountTable
            rows={chargeRows}
            isLoading={paymentsLoading}
            patientId={numericPatientId}
            chargesById={chargesById}
            submittingChargeId={submittingChargeId}
            onSubmitCob={handleSubmitCob}
            onPostForCharge={(chargeId, payerId) => setCreatePaymentTarget({
              chargeId,
              payerId: payerId ?? undefined,
            })}
            onRefresh={() => {
              refetchPayments()
              refetchCharges()
              refetchPrePayments()
            }}
          />
        </ScrollContainer>
      </StyledTableWrapper>

      {receiveOpen && (
        <ReceivePrepaymentModal
          open={receiveOpen}
          onClose={() => setReceiveOpen(false)}
          patientId={numericPatientId}
          onDone={() => {
            refetchPrePayments()
          }}
        />
      )}
      {editTarget && (
        <ReceivePrepaymentModal
          key={editTarget.id}
          open
          onClose={() => setEditTarget(null)}
          patientId={numericPatientId}
          prepayment={editTarget}
          onDone={() => {
            refetchPrePayments()
            refetchPayments()
            refetchCharges()
          }}
        />
      )}
      {applyOpen.open && applyOpen.id && (
        <ApplyPrepaymentModal
          open={applyOpen.open}
          onClose={() => setApplyOpen({ open: false })}
          patientId={numericPatientId}
          prepaymentId={applyOpen.id}
          maxAmount={Number((prepayments?.items || []).find((item: any) => item.id === applyOpen.id)?.remaining || 0)}
          onDone={() => {
            refetchPrePayments()
            refetchPayments()
            refetchCharges()
          }}
        />
      )}
      {createPaymentTarget && (
        <CreatePaymentModal
          open
          onClose={() => setCreatePaymentTarget(null)}
          patientId={numericPatientId}
          defaultChargeId={createPaymentTarget.chargeId}
          defaultPayerId={createPaymentTarget.payerId}
          accountRows={chargeRows}
          onDone={() => {
            refetchPayments()
            refetchCharges()
            refetchPrePayments()
          }}
        />
      )}
      <RefundConfirmModal
        show={Boolean(refundOpen)}
        title="Mark Pre-Payment as Refunded"
        description={
          `This will mark the unapplied amount of ${money(refundOpen?.remaining)} as refunded and `
          + 'exclude the pre-payment from the patient\'s available credit. Process the actual '
          + 'refund in Stripe (or by check/cash) first.'
        }
        onClose={() => setRefundOpen(null)}
        onConfirm={handleRefundPrepayment}
      />
    </div>
  )
}
